import React, { useEffect, useState } from 'react'

import { Box, Button, Grid } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { isEmpty } from 'lodash'

import {
  ContractStep,
  EvaluationStep,
  GeneralInfoStep,
  RisksStep,
  Stepper,
  UnsavedChangesDialog,
} from '../../../components'
import { DriveTour, LoadingFeedback } from 'components'

import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'
import GeneralInfoSchema from '../../../components/GeneralInfo/schema'
import ContractSchema from '../../../components/Contract/schema'
import RisksSchema from '../../../components/Risks/schema'

const EditForm = ({ fileList, openTour, setOpenTour }) => {
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false)

  const {
    isPreAvaliation,
    fromCreate,
    handleAdvanceStep,
    handleBack,
    handleReturnStep,
    wantsToAdvanceStep,
    wantsToReturnStep,
    companySupplier,
    activeStep,
    isLoading,
  } = useThirdPartyManagementStepper()

  const EMPTY_SCHEMA = yup.object().shape({})

  const supplier = companySupplier?.supplier || {}
  const companySupplierDetail = companySupplier?.companySupplierDetail || {}

  const defaultDocument = () => {
    const document = supplier?.document
    if (!document) return ''
    if (supplier?.international) return document
    return helpers.formatters.document.identify(document)
  }

  const schemas = isPreAvaliation
    ? [GeneralInfoSchema, EMPTY_SCHEMA, RisksSchema]
    : [GeneralInfoSchema, EMPTY_SCHEMA, ContractSchema, RisksSchema]

  const buildDefaultValues = () => ({
    name: companySupplier?.displayName || '',
    document: defaultDocument(),
    international: supplier?.international || false,
    segmentId: companySupplierDetail?.segment || null,
    description: companySupplierDetail?.description || '',
    hireDate: companySupplierDetail?.hireDate || null,
    renewDate: companySupplierDetail?.renewDate || null,
    treatmentAgentType:
      companySupplierDetail?.treatmentAgentType?.toLowerCase() || '',
    companyAgentType:
      companySupplierDetail?.companyAgentType?.toLowerCase() || '',
    attachments: fileList || [],
    riskLevel:
      helpers.thirdPartyManegementPartners.getRiskLevelByEnum(
        companySupplierDetail?.riskLevel,
      ) ?? 0,
    observation: companySupplierDetail?.observation || '',
    simplifiedRegister: companySupplier?.simplifiedRegister || false,
    hasInternationalTransfer:
      companySupplierDetail?.hasInternationalTransfer || false,
    internationalTransferDescription:
      companySupplierDetail?.internationalTransferDescription || '',
    internationalTransfers: companySupplierDetail?.internationalTransfers || [],
    suboperators: companySupplierDetail?.suboperators || [],
  })

  const formMethods = useForm({
    validationSchema: schemas[activeStep],
    defaultValues: buildDefaultValues(),
  })

  const { formState, reset } = formMethods
  const { dirtyFields } = formState

  const handleReturnStepClick = () => {
    if (!isEmpty(dirtyFields)) return setConfirmReturnOpen(true)

    handleReturnStep()
  }

  const handleConfirmReturnStep = () => {
    setConfirmReturnOpen(false)
    handleReturnStep()
  }

  const stepComponents = isPreAvaliation
    ? [<GeneralInfoStep />, <EvaluationStep />, <RisksStep />]
    : [<GeneralInfoStep />, <EvaluationStep />, <ContractStep />, <RisksStep />]

  const actionButtonText = () => {
    if (activeStep === stepComponents.length - 1) {
      return 'Finalizar'
    }

    return 'Avançar'
  }

  const { STEPS_OBJECT } = constants.tours.thirdPartyManagements
  const tourStepsMap = isPreAvaliation
    ? [
        STEPS_OBJECT.PRE_AVALIATION.CREATION.GENERAL_INFO,
        STEPS_OBJECT.PRE_AVALIATION.CREATION.EVALUATION,
        fromCreate
          ? STEPS_OBJECT.PRE_AVALIATION.CREATION.RISKS
          : STEPS_OBJECT.PRE_AVALIATION.EDIT.RISKS,
      ]
    : [
        STEPS_OBJECT.CREATION.GENERAL_INFO,
        STEPS_OBJECT.CREATION.EVALUATION,
        STEPS_OBJECT.CREATION.CONTRACT,
        fromCreate ? STEPS_OBJECT.CREATION.RISKS : STEPS_OBJECT.EDIT.RISKS,
      ]

  useEffect(() => {
    reset(buildDefaultValues())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySupplier])

  useEffect(() => {
    if (wantsToReturnStep) handleBack()
  }, [wantsToReturnStep])

  return (
    <>
      <LoadingFeedback open={isLoading} />
      <FormContext {...formMethods}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stepper />
          </Grid>

          <Grid item xs={12}>
            {stepComponents[activeStep]}
          </Grid>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="flex-end"
              gridGap={theme.spacing(2)}
            >
              <Button variant="outlined" onClick={handleReturnStepClick}>
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAdvanceStep}
                disabled={wantsToAdvanceStep}
              >
                {actionButtonText()}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </FormContext>

      <UnsavedChangesDialog
        open={confirmReturnOpen}
        setOpen={setConfirmReturnOpen}
        onConfirm={handleConfirmReturnStep}
      />

      <DriveTour
        stepsMatrix={tourStepsMap[activeStep]}
        open={openTour}
        setOpen={setOpenTour}
      />
    </>
  )
}

export default EditForm
